import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { X, MapPin, Loader2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { API } from '../utils/apiConfig';

/* ── Leaflet icon defaults (CDN) ─────────────────────────────── */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function FitToBbox({ bbox }) {
  const map = useMap();
  useEffect(() => {
    if (bbox && bbox.length === 4) {
      // bbox = [minLng, minLat, maxLng, maxLat] → leaflet wants [[lat,lng],[lat,lng]]
      map.fitBounds([[bbox[1], bbox[0]], [bbox[3], bbox[2]]], { padding: [30, 30] });
    }
  }, [bbox, map]);
  return null;
}

const corridorStyle = {
  color: '#3b82f6',
  weight: 1.5,
  opacity: 0.9,
  fillColor: '#60a5fa',
  fillOpacity: 0.18,
  dashArray: '4,3',
};
const routeStyle = { color: '#1d4ed8', weight: 4, opacity: 0.95 };

/**
 * RouteCorridorOverlay
 *  Shows the driver's route + buffered corridor on a Leaflet map.
 *  Lazy-loaded via API call — no impact on RideCard render perf.
 *
 *  Props:
 *    rideId   — UUID of the ride
 *    onClose  — close callback
 */
export const RouteCorridorOverlay = ({ rideId, onClose }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!rideId) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);

    axios
      .get(`${API}/rides/${rideId}/corridor.geojson`, { signal: controller.signal })
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.name === 'CanceledError' || e.name === 'AbortError') return;
        setError(e.response?.data?.detail || 'load_failed');
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [rideId]);

  // Split features by kind for clean rendering
  const features = data?.features || [];
  const corridorFeat = features.find((f) => f.properties?.kind === 'corridor');
  const routeFeat = features.find((f) => f.properties?.kind === 'route');
  const startFeat = features.find((f) => f.properties?.kind === 'start');
  const endFeat = features.find((f) => f.properties?.kind === 'end');

  const fallbackCenter = [51.1657, 10.4515];

  return (
    <div
      className="corridor-overlay-backdrop"
      data-testid="corridor-overlay-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.72)',
        backdropFilter: 'blur(6px)', zIndex: 10001,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div
        className="corridor-overlay-card"
        data-testid="corridor-overlay-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(960px, 100%)', height: 'min(640px, 90vh)',
          background: '#fff', borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.85rem 1.1rem', borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: '#fff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <MapPin size={18} />
            <strong style={{ fontSize: '1rem' }}>
              {t('corridor.viewRouteTitle', 'Route & Korridor')}
            </strong>
            {data?.radius_km != null && (
              <span data-testid="corridor-radius-pill" style={{
                background: 'rgba(255,255,255,0.18)', padding: '2px 10px',
                borderRadius: 999, fontSize: '0.78rem', fontWeight: 600,
              }}>
                ±{data.radius_km} km
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="corridor-overlay-close"
            aria-label="close"
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
              borderRadius: 8, padding: 6, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </header>

        <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
          {loading && (
            <div data-testid="corridor-overlay-loading" style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 10, color: '#475569', zIndex: 5,
              background: 'rgba(248,250,252,0.85)',
            }}>
              <Loader2 size={28} className="spinning" />
              <span style={{ fontSize: '0.9rem' }}>
                {t('corridor.loading', 'Korridor wird geladen…')}
              </span>
            </div>
          )}

          {error && !loading && (
            <div data-testid="corridor-overlay-error" style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 8, color: '#ef4444', padding: '1.5rem', textAlign: 'center',
            }}>
              <MapPin size={28} />
              <strong>{t('corridor.errorTitle', 'Korridor nicht verfügbar')}</strong>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                {t(`corridor.errors.${error}`, t('corridor.errors.load_failed', 'Bitte später erneut versuchen.'))}
              </span>
            </div>
          )}

          {!loading && !error && data && (
            <MapContainer
              center={fallbackCenter}
              zoom={6}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom
              data-testid="corridor-overlay-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitToBbox bbox={data.bbox} />

              {corridorFeat && (
                <GeoJSON
                  data={corridorFeat}
                  style={() => corridorStyle}
                  data-testid="corridor-polygon"
                />
              )}
              {routeFeat && (
                <GeoJSON
                  data={routeFeat}
                  style={() => routeStyle}
                  data-testid="corridor-route"
                />
              )}
              {startFeat && (
                <Marker
                  position={[startFeat.geometry.coordinates[1], startFeat.geometry.coordinates[0]]}
                  icon={startIcon}
                >
                  <Popup>
                    <strong>{t('common.from', 'Start')}:</strong> {startFeat.properties.label}
                  </Popup>
                </Marker>
              )}
              {endFeat && (
                <Marker
                  position={[endFeat.geometry.coordinates[1], endFeat.geometry.coordinates[0]]}
                  icon={endIcon}
                >
                  <Popup>
                    <strong>{t('common.to', 'Ziel')}:</strong> {endFeat.properties.label}
                  </Popup>
                </Marker>
              )}

              {/* Legend */}
              <div style={{
                position: 'absolute', bottom: 12, left: 12, zIndex: 1000,
                background: 'rgba(255,255,255,0.95)', borderRadius: 8,
                padding: '8px 12px', fontSize: '0.78rem', color: '#0f172a',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                display: 'flex', flexDirection: 'column', gap: 4,
              }} data-testid="corridor-legend">
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 18, height: 4, background: '#1d4ed8', borderRadius: 2 }} />
                  {t('corridor.legendRoute', 'Geplante Route')}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    width: 18, height: 10, background: 'rgba(96,165,250,0.35)',
                    border: '1px dashed #3b82f6', borderRadius: 2,
                  }} />
                  {t('corridor.legendCorridor', 'Korridor (Mitfahr-Bereich)')}
                </span>
              </div>
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouteCorridorOverlay;
